import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useI18n, useTheme } from '@react/hooks';
import type { ThemeMode } from '@react/hooks';
import type { Userprofile } from '@core/types/userprofile';
import { authStore } from '@core/stores';
import { ListUserprofilesForService } from '@core/api/endpoints/userprofile/list_userprofiles_for_service';

interface TenantSwitcherProps {
  onClose: () => void;
}

// --- Icon components ---

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function LogOutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  ru: 'Русский',
  zh: '中文',
};

const THEME_MODES: { mode: ThemeMode; labelKey: string; Icon: React.FC<{ className?: string }> }[] = [
  { mode: 'system', labelKey: 'layout.tenant.systemMode', Icon: MonitorIcon },
  { mode: 'light', labelKey: 'layout.tenant.lightMode', Icon: SunIcon },
  { mode: 'dark', labelKey: 'layout.tenant.darkMode', Icon: MoonIcon },
];

export const TenantSwitcher = observer(({ onClose }: TenantSwitcherProps) => {
  const auth = useAuth();
  const { t, locale, supportedLocales, setLocale } = useI18n();
  const theme = useTheme();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState<Userprofile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  // Load current user's profiles on mount
  useEffect(() => {
    if (!auth.userId) return;
    setProfilesLoading(true);
    ListUserprofilesForService(auth.userId, { pagination: { limit: 20, cursor: '', direction: 'forward' } } as never)
      .then((resp) => {
        setProfiles(resp.userprofiles?.profiles ?? []);
      })
      .catch(() => {})
      .finally(() => setProfilesLoading(false));
  }, [auth.userId]);

  // TODO: Replace with real tenant list from TenantStore
  const tenants = [
    {
      id: 'current',
      name: auth.user?.username ? `${auth.user.username}'s Workspace` : t('layout.tenant.defaultWorkspace'),
      role: 'owner',
      active: true,
    },
  ];

  const handleSwitchTenant = (tenantId: string) => {
    // TODO: auth.switchTenant(tenantId) → refresh context → navigate('/inbox')
    void tenantId;
    onClose();
  };

  const handleSwitchProfile = (profileId: string) => {
    authStore.switchProfile(profileId);
    onClose();
    // Reload current page to apply new profile context
    window.location.reload();
  };

  const handleCreateWorkspace = () => {
    navigate('/userprofiles/new');
    onClose();
  };

  const handleOpenProfile = () => {
    navigate('/settings/profile');
    onClose();
  };

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        className="absolute bottom-16 left-4 md:left-14 w-72 max-h-[calc(100vh-5rem)] overflow-y-auto bg-card rounded-lg border border-border shadow-xl"
        role="menu"
        aria-label={t('layout.tenant.title')}
      >
        {/* User info */}
        <div className="px-4 py-3 border-b border-border">
          <p className="text-sm font-medium text-foreground truncate">
            {auth.user?.username || t('common.user')}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {auth.user?.email}
          </p>
        </div>

        {/* My Profile */}
        <div className="p-1 border-b border-border">
          <button
            role="menuitem"
            className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
            onClick={handleOpenProfile}
          >
            <UserIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span className="truncate flex-1">{t('layout.tenant.myProfile')}</span>
          </button>
        </div>

        {/* Workspaces */}
        <div className="p-1 border-b border-border">
          <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t('layout.tenant.workspaces')}
          </p>
          {tenants.map((tenant) => (
            <button
              key={tenant.id}
              role="menuitem"
              className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
              onClick={() => handleSwitchTenant(tenant.id)}
            >
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                {tenant.active && <CheckIcon className="h-3.5 w-3.5 text-primary" />}
              </span>
              <span className="truncate flex-1">{tenant.name}</span>
              <span className="text-xs text-muted-foreground capitalize">{tenant.role}</span>
            </button>
          ))}
        </div>

        {/* Profiles */}
        <div className="p-1 border-b border-border">
          <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t('layout.tenant.profiles')}
          </p>
          {profilesLoading ? (
            <div className="px-3 py-2 text-center">
              <div className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : profiles.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t('layout.tenant.noProfiles')}</p>
          ) : (
            profiles.map((profile) => {
              const isActive = profile.entity?.id === auth.activeProfileId;
              const displayName = profile.display_name || [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.entity?.id?.slice(0, 8);
              const initials = `${(profile.first_name || '?').charAt(0)}${(profile.last_name || '?').charAt(0)}`.toUpperCase();
              return (
                <button
                  key={profile.entity?.id}
                  role="menuitem"
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
                  onClick={() => profile.entity?.id && handleSwitchProfile(profile.entity.id)}
                >
                  <span className="w-6 h-6 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary">
                    {initials}
                  </span>
                  <span className="truncate flex-1">{displayName}</span>
                  {isActive && <CheckIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                  {profile.is_default && (
                    <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{t('layout.tenant.default')}</span>
                  )}
                </button>
              );
            })
          )}
          <button
            role="menuitem"
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors text-left"
            onClick={handleCreateWorkspace}
          >
            <span className="w-6 h-6 flex-shrink-0 rounded-full border border-dashed border-muted-foreground/40 flex items-center justify-center">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </span>
            {t('layout.tenant.createProfile')}
          </button>
        </div>

        {/* Appearance */}
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            {t('layout.tenant.appearance')}
          </p>
          <div className="flex gap-1">
            {THEME_MODES.map(({ mode, labelKey, Icon }) => (
              <button
                key={mode}
                onClick={() => theme.setMode(mode)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  theme.mode === mode
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Language */}
        {supportedLocales.length > 1 && (
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              {t('layout.tenant.language')}
            </p>
            <div className="flex gap-1">
              {supportedLocales.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLocale(lang)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    locale === lang
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {LANGUAGE_LABELS[lang] || lang}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sign out */}
        <div className="p-1">
          <button
            onClick={handleLogout}
            role="menuitem"
            className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-destructive hover:bg-destructive/10 transition-colors text-left"
          >
            <LogOutIcon className="h-4 w-4" />
            {t('layout.tenant.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
});
